//@author: mandy

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnInit,
    ViewEncapsulation,
    Output,
    EventEmitter,
    ViewChild,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { HierarchyUtil } from "./s25.event.type.hierarchy.util";
import { TypeMap, Type, ChosenModels } from "./s25.event.type.util";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { Table } from "../s25-table/Table";
import { Bind } from "../../decorators/bind.decorator";
import { S25EditableDateComponent } from "../s25-editable/s25-editable-date/s25.editable.date.component";
import { S25ExpandedContentComponent } from "./s25.expanded.content.component";
import { GenericTableRenderHTMLComponent } from "../s25-table/generics/generic.table.render.html.component";
import { EventData, EventTypeI } from "../../services/event.service";
import { TablePopoverButtonComponent } from "../s25-table/generics/table.popover.button.component";
import { S25UsageComponent } from "../../standalone/s25.usage.component";
import { MasterDefinitionService } from "../../services/master.definitions/master.definition.service";

@TypeManagerDecorator("s25-ng-event-type-list-view")
@Component({
    selector: "s25-ng-event-type-list-view",
    template: `
        @if (init) {
            <div class="c-margin-bottom--single c-margin-top--single">
                <!-- Buttons list -->
                @for (item of modeTypes; track item) {
                    <button
                        (click)="refreshList(item.type, item.itemName); filter = ''"
                        class="btn btn-default"
                        [ngClass]="{
                            ngCompview: true,
                            'btn-primary': item.type === type,
                            foldersTab: item.type === 'F',
                        }"
                    >
                        {{ item.itemName }}
                    </button>
                }

                <!-- Create New button -->
                <button class="aw-button aw-button--outline  button-spacing" (click)="createNew()">Create New</button>

                <!-- Create New button -->
                <button
                    class="aw-button aw-button--outline  button-spacing"
                    (click)="bulkEdit()"
                    [disabled]="selectedItems.length === 0"
                >
                    Bulk Edit
                </button>
            </div>

            @if (type === "F") {
                <div class="c-margin-bottom--single">
                    Filter By History:
                    <select
                        class="cn-form__control c-margin-top--quarter"
                        [(ngModel)]="selectedDate"
                        (change)="changeDate()"
                    >
                        @for (m of modeDates; track m) {
                            <option [ngValue]="m.itemId">
                                {{ m.itemName }}
                            </option>
                        }
                    </select>
                </div>
            }

            <s25-ng-table
                [dataSource]="tableConfig"
                [columnSortable]="true"
                [hasFilter]="true"
                [hasRefresh]="true"
                [pivotThreshold]="800"
                [hasSelect]="true"
                [hasSelectAll]="true"
                [hasColumnChooser]="true"
                [noResultsMessage]="'No Results Found'"
                (selectedChange)="selectedChange($event)"
            ></s25-ng-table>
        }
    `,
    styles: `
        .button-spacing {
            margin-left: 50px;
        }

        s25-ng-event-type-list-view .ngCompview {
            text-shadow: none !important;
            &:hover {
                cursor: pointer;
            }
        }

        .foldersTab {
            border-radius: 0 3px 3px 0 !important;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EventTypeListViewComponent implements OnInit {
    @Input() type = Type.EventType;
    @Output() valChange = new EventEmitter();
    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;

    init = false;
    selectedDate = 1;
    chosenModels: ChosenModels[] = [];
    mode = "Event Types";
    filter = "";
    class = "etl--create-new";
    modeDateData = { itemId: 1, itemName: "Current Year Data" };
    sortOrderView = false;
    optListCheck = true;
    userName = "";
    groupId = 0;

    modeDates = [
        { itemId: 1, itemName: "Current Year Data" },
        { itemId: 5, itemName: "5 Years Data" },
        { itemId: 7, itemName: "7 Years Data" },
        { itemId: 10, itemName: "10 Years Data" },
        { itemId: 15, itemName: "15 Years Data" },
        { itemId: 20, itemName: "20 Years Data" },
    ];
    modeTypes = [
        { itemId: 1, itemName: "Event Types", type: "E", itemTypeId: 7, valName: "eventType" },
        { itemId: 2, itemName: "Cabinets", type: "C", itemTypeId: 11, valName: "cabinet" },
        { itemId: 3, itemName: "Folders", type: "F", itemTypeId: 12, valName: "folder" },
    ];
    modeType = this.modeTypes[0];

    getDataDate: number = Number(new Date().getFullYear() - 1 + "1231"); //'20171231';
    tableConfig: Table.DataSource;

    data: EventData[] | EventTypeI[] = [];
    selectedItems: number[] = [];

    action: string = undefined;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    async ngOnInit() {
        this.tableConfig = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: TypeMap[this.type].col,
        };
        this.init = true;
        this.cd.detectChanges();
    }

    @Bind
    async getRows(query: Table.PaginatedQuery): Promise<Table.DataSourceResponse> {
        this.data =
            (await (this.type === Type.Folder
                ? TypeMap[this.type].service(this.getDataDate)
                : TypeMap[this.type].service())) || [];

        this.data = S25Util.array.forceArray(this.data);
        if (this.type === Type.EventType)
            this.data = this.data.filter(
                (i): i is EventTypeI => "type_id" in i && typeof i.type_id === "number" && i.type_id > 0,
            ); // event type filter negative ids ,  but sitll need cabinets and folders
        const filterDdata = this.type === Type.EventType ? this.data.filter((i) => i.node_type === "E") : this.data; // this list filtered not cabinets & folder for list view
        const searchQuerydata = S25Util.array.forceArray(filterDdata || []);
        return {
            rows: searchQuerydata.map((row) => this.mapToRow(row)),
        };
    }

    @Bind
    mapToRow(row: any): Table.Row {
        const cells: Table.Row["cells"] = {};
        const { id, itemName } = this.getIdAndName(row);

        for (let i = 0; i < this.tableConfig.columns.length; i++) {
            const [key, val] = this.getCell(this.tableConfig.columns[i], row);
            cells[key] = val;
        }
        return {
            id: id,
            name: itemName,
            cells,
        };
    }

    getCell(column: any, row: any): [string, Table.Cell] {
        const typeId =
            this.modeType.type === Type.EventType
                ? row.type_id
                : this.modeType.type === Type.Cabinet
                  ? row.cabinet_id
                  : this.modeType.type === Type.Folder
                    ? row.event_id
                    : undefined;
        switch (column.id) {
            case "type_name":
                const editurl = this.getHyperLink(typeId, "edit");
                return [
                    "type_name",
                    {
                        component: GenericTableRenderHTMLComponent,
                        inputs: { text: `<a href="${editurl}">${row.type_name}</a>`, isHtml: true },
                        textValue: row.type_name,
                    },
                ];

            case "cabinet_name":
                return ["cabinet_name", { text: "cabinet_name" }];

            case "cabinets":
                return [
                    "cabinets",
                    {
                        component: S25ExpandedContentComponent,
                        inputs: { type: "C", typeId: typeId, listData: this.data, itemTypeId: this.modeType.itemId },
                    },
                ];

            case "req":
                return [
                    "req",
                    {
                        component: S25ExpandedContentComponent,
                        inputs: { type: "req", typeId: typeId, listData: this.data, itemTypeId: this.modeType.itemId },
                    },
                ];

            case "event_name":
                const eventNameurl = this.getHyperLink(typeId, "edit");
                return [
                    "event_name",
                    {
                        component: GenericTableRenderHTMLComponent,
                        inputs: { text: `<a href="${eventNameurl}">${row.event_name}</a>`, isHtml: true },
                        textValue: row.event_name,
                    },
                ];

            case "custAtt":
                return [
                    "custAtt",
                    {
                        component: S25ExpandedContentComponent,
                        inputs: {
                            type: "custAtt",
                            typeId: typeId,
                            listData: this.data,
                            itemTypeId: this.modeType.itemId,
                        },
                    },
                ];

            case "event_type_name":
                return [
                    "event_type_name",
                    {
                        text: row.event_type_name,
                    },
                ];

            case "parent_name":
                return [
                    "parent_name",
                    {
                        text: row.parent_name,
                    },
                ];

            case "start_date":
                return [
                    "start_date",
                    {
                        component: S25EditableDateComponent,
                        inputs: { val: row.start_date, readOnly: true },
                        textValue: row.start_date?.toString() ?? "",
                        sortValue: S25Util.date.toS25ISODateTimeStr(row.start_date),
                    },
                ];

            case "end_date":
                return [
                    "end_date",
                    {
                        component: S25EditableDateComponent,
                        inputs: { val: row.end_date, readOnly: true },
                        textValue: row.end_date?.toString() ?? "",
                        sortValue: S25Util.date.toS25ISODateTimeStr(row.end_date),
                    },
                ];

            case "organization_name":
                return [
                    "organization_name",
                    {
                        text: row.organization_name,
                    },
                ];

            case "folders":
                return [
                    "folders",

                    {
                        component: S25ExpandedContentComponent,
                        inputs: { type: "F", typeId: typeId, listData: this.data, itemTypeId: this.modeType.itemId },
                    },
                ];

            case "usage":
                return [
                    "usage",
                    {
                        component: TablePopoverButtonComponent,
                        inputs: {
                            label: "Usage",
                            cell: {
                                component: S25UsageComponent,
                                inputs: {
                                    service: MasterDefinitionService.usageService.bind(this, "event_type", row.type_id),
                                },
                            },
                        },
                    },
                ];

            case "copy":
                const copyurl = this.getHyperLink(typeId, "copy");
                return [
                    "copy",
                    {
                        component: GenericTableRenderHTMLComponent,
                        inputs: { text: `<a href="${copyurl}">Copy</a>`, isHtml: true },
                    },
                ];

            case "hierarchy":
                const viewurl = this.getHyperLink(typeId, "view");
                return [
                    "hierarchy",
                    {
                        component: GenericTableRenderHTMLComponent,
                        inputs: { text: `<a href="${viewurl}">Hierarchy</a>`, isHtml: true },
                    },
                ];
        }
    }

    selectedChange($event: number[]) {
        this.selectedItems = [...$event];
        this.cd.detectChanges();
    }

    refreshList(type: Type, itemName?: string) {
        this.selectedItems = [];
        this.chosenModels = [];
        this.action = undefined;
        this.type = type;
        this.tableConfig.columns = TypeMap[this.type].col;
        if (itemName)
            this.valChange.emit({
                itemName: itemName,
                type: type,
                itemTypId: this.modeType.itemTypeId,
                selectedItems: this.selectedItems,
                chosenModels: this.chosenModels,
                action: this.action,
            });
        this.modeType = this.modeTypes.find((mt) => mt.type === type) || this.modeTypes[0];
        this.init = false;
        this.cd.detectChanges();
        this.init = true;
        this.cd.detectChanges();
    }

    createNew() {
        let basePath = "";
        let pathSuffix = "";
        switch (this.type) {
            case "C":
                basePath = HierarchyUtil.getCompUrl("cabinetAdvForm");
                pathSuffix = "/new/C/0";
                break;
            case "F":
                basePath = HierarchyUtil.getCompUrl("folderAdvForm");
                pathSuffix = "/new/F/0";
                break;
            default:
                basePath = HierarchyUtil.getCompUrl("eventType");
                pathSuffix = "/new/E/0";
        }
        const createNewUrl = basePath + pathSuffix;
        window.location.assign(createNewUrl);
    }

    changeDate() {
        this.getDataDate = Number(`${new Date().getFullYear() - this.selectedDate}1231`);
        this.refreshList(this.type);
    }

    @Bind
    getIdAndName(item: any) {
        let id, itemName;
        if ("type_id" in item) {
            id = item.type_id;
            itemName = item.type_name?.toString() ?? "";
        } else if ("node_type" in item && item.node_type === "C") {
            id = item.cabinet_id;
            itemName = item.cabinet_name?.toString() ?? "";
        } else if ("node_type" in item && item.node_type === "F") {
            id = item.event_id;
            itemName = item.event_name?.toString() ?? "";
        }
        return { id, itemName };
    }

    bulkEdit() {
        for (let i = 0; i < this.selectedItems.length; i++) {
            const findItem = (this.data as EventData[]).find((item) => item.event_id === this.selectedItems[i]);
            if (findItem) {
                this.chosenModels.push({
                    itemId: findItem.event_id,
                    itemName: findItem.event_name,
                    itemStartDate: S25Util.date.parse(findItem.start_date),
                    itemEndDate: S25Util.date.parse(findItem.end_date),
                    itemParentId: findItem.parent_id,
                    itemEventTypeId: findItem.event_type_id,
                });
            }
        }
        this.action = "bulkEdit";
        this.valChange.emit({
            itemName: this.modeType.itemName,
            type: this.modeType.type,
            itemTypId: this.modeType.itemTypeId,
            selectedItems: this.selectedItems,
            chosenModels: this.chosenModels,
            action: this.action,
        });
    }

    getHyperLink(itemId: number, mode: string) {
        const basePath =
            this.type === Type.Folder
                ? HierarchyUtil.getCompUrl(this.modeType.valName, this.getDataDate)
                : HierarchyUtil.getCompUrl(this.modeType.valName);
        const pathSuffix = `${basePath}/${this.modeType.itemId}/${mode}/${this.modeType.type}/${itemId}`;

        return pathSuffix;
    }
}
